import { useQueryClient } from "@tanstack/react-query";
import { lazy, useMemo } from "react";
import { RouterProvider, createBrowserRouter } from "react-router";

import { DashboardContainer } from "./layout/DashboardContainer";

import { paths } from "@/config/paths";
import { ProtectedOnboardingRoute, ProtectedRoute } from "@/lib/auth";

import type { QueryClient } from "@tanstack/react-query";
import type { FC } from "react";

const PlansPage = lazy(() =>
  import("./pages/Plans.page").then((module) => ({
    default: module.default,
  }))
);

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.home.path,
      lazy: () => import("./pages/Landing.page").then(convert(queryClient)),
    },
    {
      path: paths.auth.login.path,
      lazy: () => import("./pages/Login.page").then(convert(queryClient)),
    },
    {
      path: paths.auth.passwordRecover.path,
      lazy: () => import("./pages/PasswordRecover.page").then(convert(queryClient)),
    },
    {
      path: paths.auth.setNewPasswordRecover.path,
      lazy: () => import("./pages/NewPasswordRecover.page").then(convert(queryClient)),
    },
    {
      path: "/auth/activation/:registration_token",
      lazy: () => import("./pages/Activation.page").then(convert(queryClient)),
    },
    {
      path: paths.auth.signUp.path,
      lazy: () => import("./pages/SignUp.page").then(convert(queryClient)),
    },
    {
      path: paths.plans.root.path,
      element: (
        <ProtectedRoute>
          <PlansPage />
        </ProtectedRoute>
      ),
    },
    {
      path: paths.app.root.path,
      element: (
        <ProtectedOnboardingRoute>
          <DashboardContainer />
        </ProtectedOnboardingRoute>
      ),
      // ErrorBoundary
      children: [
        {
          path: paths.app.dashboard.path,
          lazy: () => import("./pages/Dashboard.page").then(convert(queryClient)),
        },
        {
          path: paths.app.garage.root.path,
          children: [
            {
              path: paths.app.garage.car.path,
              children: [
                {
                  path: ":tabId",
                  lazy: () => import("./pages/GarageCar.page").then(convert(queryClient)),
                },
                {
                  index: true,
                  lazy: () => import("./pages/GarageCar.page").then(convert(queryClient)),
                },
              ],
            },
            {
              path: ":tabId",
              lazy: () => import("./pages/Garage.page").then(convert(queryClient)),
            },
            {
              index: true,
              lazy: () => import("./pages/Garage.page").then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.app.compare.path,
          lazy: () => import("./pages/Compare.page").then(convert(queryClient)),
        },
        {
          path: paths.app.markets.root.path,
          lazy: () => import("./pages/Market.page").then(convert(queryClient)),
        },
        {
          path: paths.app.markets.makes.path,
          lazy: () => import("./pages/Makes.page").then(convert(queryClient)),
        },
        {
          path: paths.app.markets.makeDetails.path,
          lazy: () => import("./pages/MakeDetails.page").then(convert(queryClient)),
        },
        {
          path: paths.app.markets.car.path,
          children: [
            {
              path: ":tabId",
              lazy: () => import("./pages/MarketCar.page").then(convert(queryClient)),
            },
            {
              index: true,
              lazy: () => import("./pages/MarketCar.page").then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.app.markets.compare.path,
          lazy: () => import("./pages/MarketCompare.page").then(convert(queryClient)),
        },
        {
          path: paths.app.auction.root.path,
          children: [
            {
              path: paths.app.auction.details.path,
              children: [
                {
                  index: true,
                  lazy: () => import("./pages/AuctionDetails.page").then(convert(queryClient)),
                },
              ],
            },
            {
              index: true,
              lazy: () => import("./pages/Auction.page").then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.app.documents.path,
          lazy: () => import("./pages/Documents.page").then(convert(queryClient)),
        },
        {
          path: paths.app.vin.car.path,
          children: [
            {
              path: ":tabId",
              lazy: () => import("./pages/VinCar.page").then(convert(queryClient)),
            },
            {
              index: true,
              lazy: () => import("./pages/VinCar.page").then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.app.profile.path,
          children: [
            {
              path: ":tabId",
              lazy: () => import("./pages/Profile.page").then(convert(queryClient)),
            },
            {
              index: true,
              lazy: () => import("./pages/Profile.page").then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.app.formerCars.root.path,
          children: [
            {
              path: paths.app.formerCars.car.path,
              children: [
                {
                  path: ":tabId",
                  lazy: () => import("./pages/FormerCar.page").then(convert(queryClient)),
                },
                {
                  index: true,
                  lazy: () => import("./pages/FormerCar.page").then(convert(queryClient)),
                },
              ],
            },
            {
              index: true,
              lazy: () => import("./pages/FormerCars.page").then(convert(queryClient)),
            },
          ],
        },
        {
          // Debug url
          path: paths.app.components.path,
          lazy: () => import("./pages/Components.page").then(convert(queryClient)),
        },
      ],
    },
    {
      path: "*",
      lazy: () => import("./pages/NotFound.page").then(convert(queryClient)),
    },
  ]);

export const AppRouter: FC = () => {
  const queryClient = useQueryClient();
  const router = useMemo(() => {
    return createAppRouter(queryClient);
  }, [queryClient]);

  return <RouterProvider router={router} />;
};
